/**
 * What: Draws a Worker-sent `tiles` block: a grid of choice tiles, one of them ticked. When the Worker marks the block with
 *   `collapse`, only the first `visible` tiles show, behind a "Show more"/"Show fewer" toggle; expanding is local UI state, no
 *   round trip.
 * Transport / data-flow: Pure presentation. `chosen` is already the pending-overlaid choice; a tile that names a client
 *   handler runs it instead of reporting a choice. Any message under the tiles is drawn by `BlockMessage`, like every field.
 */
import { useState } from 'react'
import type { BuilderBlock, ClientAction } from '@testfeed/shopify-contracts'
import { BUILDER_BOX_GRID_COLUMNS, BuilderBox } from './BuilderBox'
import type { OnEdit } from './SectionBlocks'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../shopifyUiTracking'

export function TilesBlock({
  block,
  chosen,
  disabled,
  onEdit,
  clientActions,
}: {
  block: Extract<BuilderBlock, { kind: 'tiles' }>
  chosen: string | null
  disabled: boolean
  onEdit: OnEdit
  clientActions: Readonly<Record<ClientAction, (arg?: string) => void>>
}) {
  const shopify = useAppBridge()
  const [expanded, setExpanded] = useState(false)
  const collapsing = Boolean(block.collapse && block.tiles.length > block.collapse.visible)
  const shown = collapsing && !expanded ? block.tiles.slice(0, block.collapse!.visible) : block.tiles
  return (
    <s-stack direction="block" gap="base">
      <s-grid gridTemplateColumns={BUILDER_BOX_GRID_COLUMNS} gap="base">
        {shown.map((tile) => {
          const selected = tile.id === chosen
          return (
            <BuilderBox
              key={tile.id}
              icon={tile.icon ?? undefined}
              title={tile.title}
              description={tile.detail}
              trailing={selected ? <s-icon type="check-circle" tone="success" /> : undefined}
              selected={selected}
              disabled={tile.disabled || disabled}
              onSelect={() => {
                if (tile.client) clientActions[tile.client](tile.id)
                else onEdit(block.key, tile.id)
              }}
            >
              {tile.image ? (
                <s-box blockSize="140px" borderRadius="base" overflow="hidden" background="subdued">
                  <s-image src={tile.image.src} alt={tile.image.alt} inlineSize="fill" objectFit="contain" />
                </s-box>
              ) : null}
              {tile.body ? <s-text color="subdued">{tile.body}</s-text> : null}
              {tile.badge ? (
                <s-box>
                  <s-badge tone={tile.badge.tone ?? undefined}>{tile.badge.text}</s-badge>
                </s-box>
              ) : null}
            </BuilderBox>
          )
        })}
      </s-grid>
      {collapsing ? (
        <s-stack direction="inline">
          <s-button
            variant="tertiary"
            onClick={() => trackShopifyAction(shopify, 'toggle-options', () => setExpanded((open) => !open))}
          >
            {expanded ? block.collapse!.fewer : block.collapse!.more}
          </s-button>
        </s-stack>
      ) : null}
    </s-stack>
  )
}
